/**
 * Переліки стовпців — тут, поруч із перетворенням рядка на сутність.
 *
 * Одне місце, бо копія, яка розійдеться з цією, не дасть ні помилки
 * компіляції, ні падіння запиту: `SELECT` просто поверне на поле менше,
 * а `toEntry` прочитає з нього `undefined`. Тобто мовчазна втрата поля
 * рівно там, де її найважче помітити.
 */
export const ENTRY_COLUMNS = "id, kind, title, body, entry_date, mood, created_at, updated_at, deleted_at, hlc, device_id";
export const ITEM_COLUMNS = "id, horizon_id, text, done_at, ordinal, rolled_from_id, span_end_key, start_time, end_time, calendar_event_id, created_at, updated_at, deleted_at, hlc, device_id";
export const HORIZON_COLUMNS = "id, kind, key, title, created_at, updated_at, deleted_at, hlc, device_id";
/** Той самий перелік із псевдонімом таблиці — для запитів зі зʼєднанням. */
export function prefixed(columns, alias) {
    return columns
        .split(", ")
        .map((c) => `${alias}.${c}`)
        .join(", ");
}
export class RowShapeError extends Error {
    constructor(column, got) {
        super(`несподіване значення у стовпці ${column}: ${got === null ? "NULL" : typeof got}`);
        this.name = "RowShapeError";
    }
}
export function str(row, column) {
    const v = row[column];
    if (typeof v !== "string")
        throw new RowShapeError(column, v ?? null);
    return v;
}
export function strOrNull(row, column) {
    const v = row[column];
    if (v === null || v === undefined)
        return null;
    if (typeof v !== "string")
        throw new RowShapeError(column, v);
    return v;
}
export function num(row, column) {
    const v = row[column];
    if (typeof v !== "number")
        throw new RowShapeError(column, v ?? null);
    return v;
}
export function numOrNull(row, column) {
    const v = row[column];
    if (v === null || v === undefined)
        return null;
    if (typeof v !== "number")
        throw new RowShapeError(column, v);
    return v;
}
function bytesOrNull(row, column) {
    const v = row[column];
    if (v === null || v === undefined)
        return null;
    if (!(v instanceof Uint8Array))
        throw new RowShapeError(column, v);
    return v;
}
/**
 * `horizon_item.text` — BLOB, а не TEXT, хоча домен бачить рядок.
 *
 * Так закладено навмисно, і тепер це працює: коли ввімкнено `CipherPort`,
 * у стовпці лежить конверт шифротексту ([ADR-0015]), інакше — той самий
 * текст у UTF-8. Тип стовпця в обох випадках той самий, тож увімкнення
 * шифрування не потребує міграції вже наповненої бази.
 */
const utf8 = { enc: new TextEncoder(), dec: new TextDecoder() };
export function encodeText(text) {
    return utf8.enc.encode(text);
}
export function decodeText(bytes) {
    return utf8.dec.decode(bytes);
}
/**
 * Сирі байти стовпця — до будь-якого тлумачення.
 *
 * Розкодувати BLOB як UTF-8 «про всяк випадок» не можна: шифротекст після
 * такого перетворення вже не відновити, бо невалідні послідовності байтів
 * замінюються на U+FFFD безповоротно. Тому текст робиться з байтів явно
 * і лише тоді, коли відомо, що це текст.
 */
export function textBytes(row, column) {
    const v = row[column];
    if (v instanceof Uint8Array)
        return v;
    // Драйвери розходяться в тому, чи віддавати BLOB як байти: node-шлях може
    // повернути рядок. Приймаємо обидва, але мовчки не ковтаємо число чи NULL.
    if (typeof v === "string")
        return utf8.enc.encode(v);
    throw new RowShapeError(column, v ?? null);
}
const ENTRY_KINDS = ["journal", "note", "inbox"];
const HORIZON_KINDS = ["year", "month", "week", "day"];
export function toEntry(row) {
    const kind = str(row, "kind");
    if (!ENTRY_KINDS.includes(kind))
        throw new RowShapeError("kind", kind);
    return {
        id: str(row, "id"),
        kind: kind,
        title: strOrNull(row, "title"),
        body: bytesOrNull(row, "body"),
        entryDate: strOrNull(row, "entry_date"),
        mood: numOrNull(row, "mood"),
        createdAt: num(row, "created_at"),
        updatedAt: num(row, "updated_at"),
        deletedAt: numOrNull(row, "deleted_at"),
        hlc: str(row, "hlc"),
        deviceId: str(row, "device_id"),
    };
}
export function toHorizonRow(row) {
    const kind = str(row, "kind");
    if (!HORIZON_KINDS.includes(kind))
        throw new RowShapeError("kind", kind);
    return {
        id: str(row, "id"),
        kind: kind,
        key: str(row, "key"),
        title: strOrNull(row, "title"),
        createdAt: num(row, "created_at"),
        updatedAt: num(row, "updated_at"),
        deletedAt: numOrNull(row, "deleted_at"),
        hlc: str(row, "hlc"),
        deviceId: str(row, "device_id"),
    };
}
/**
 * Рядок плану. Текст приходить окремим аргументом, бо в базі він може бути
 * шифротекстом — і тоді розшифрувати його вміє лише репозиторій із ключем.
 */
export function toHorizonItem(row, text) {
    return {
        id: str(row, "id"),
        horizonId: str(row, "horizon_id"),
        text,
        doneAt: numOrNull(row, "done_at"),
        ordinal: num(row, "ordinal"),
        rolledFromId: strOrNull(row, "rolled_from_id"),
        spanEndKey: strOrNull(row, "span_end_key"),
        startTime: strOrNull(row, "start_time"),
        endTime: strOrNull(row, "end_time"),
        calendarEventId: strOrNull(row, "calendar_event_id"),
        createdAt: num(row, "created_at"),
        updatedAt: num(row, "updated_at"),
        deletedAt: numOrNull(row, "deleted_at"),
        hlc: str(row, "hlc"),
        deviceId: str(row, "device_id"),
    };
}
//# sourceMappingURL=rows.js.map